import { ref } from 'vue';
// eslint-disable-next-line
import Worker from 'worker-loader!@/workers/qr-reader-worker.js';

const FACING_MODE_REGEXP = {
  environment: /rear|back|environment/i,
  user: /front|user|face/i,
};

const qrCodeContent = ref(null);
const scanRegionArea = ref({});
let video = null;
let canvasElement = null;
let canvas = null;
let worker = null;
let stream = null;
let animationId = null;
let lastCheckTimestamp = Date.now();
const CHECK_INTERVAL = 1000 / 4;

function updateOverlay() {
  const { videoWidth, videoHeight } = video;
  const videoAspectRatio = videoWidth / videoHeight;
  const availableWidth = window.innerWidth;
  const availableHeight = window.innerHeight;
  let width;
  let height;

  if (availableWidth / availableHeight > videoAspectRatio) {
    // Calculate based on available width
    height = Math.min(availableHeight, videoHeight);
    width = height * videoAspectRatio;
  } else {
    // Calculate based on available height
    width = Math.min(availableWidth, videoWidth);
    height = width / videoAspectRatio;
  }
  const top = (availableHeight - height) / 2;
  const left = (availableWidth - width) / 2;

  scanRegionArea.value.width = width;
  scanRegionArea.value.height = height;
  scanRegionArea.value.x = left;
  scanRegionArea.value.y = top;
}

function tick() {
  const timestamp = Date.now();
  if (timestamp - lastCheckTimestamp < CHECK_INTERVAL) {
    animationId = requestAnimationFrame(tick);
    return;
  }

  lastCheckTimestamp = timestamp;
  canvas.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
  const imageData = canvas.getImageData(0, 0, video.videoWidth, video.videoHeight);
  worker.postMessage({ data: imageData.data, width: imageData.width, height: imageData.height });
  animationId = requestAnimationFrame(tick);
}

export default function useQrReader() {
  function stopStream(s) {
    const [track] = s.getVideoTracks();
    track.stop();
  }

  async function listCameras(requestLabels) {
    if (!navigator.mediaDevices) return [];

    const enumerateCameras = async () => (await navigator.mediaDevices.enumerateDevices())
      .filter((device) => device.kind === 'videoinput');

    let openedStream;
    try {
      if (requestLabels && (await enumerateCameras()).every((camera) => !camera.label)) {
        openedStream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true });
      }
    } catch (e) {
      // Fail gracefully, especially if the device has no camera or on mobile when the camera is already in use
      // and some browsers disallow a second stream.
    }

    try {
      return (await enumerateCameras()).map((camera, i) => ({
        id: camera.deviceId,
        label: camera.label || (i === 0 ? 'Default Camera' : `Camera ${i + 1}`),
      }));
    } finally {
      // close the stream we just opened for getting camera access for listing the device labels
      if (openedStream) {
        stopStream(openedStream);
      }
    }
  }

  function getCameraId(allCameras, facingMode = 'environment') {
    const facingModeCondition = FACING_MODE_REGEXP[facingMode];
    if (!allCameras?.length) {
      return null;
    }

    if (allCameras.length === 1) {
      return allCameras[0].id;
    }

    if (facingMode === 'user') {
      return allCameras.find((cam) => facingModeCondition.test(cam.label))?.id || allCameras[0].id;
    }

    const backFacingCameras = allCameras.filter((cam) => facingModeCondition.test(cam.label));
    return backFacingCameras.length
      ? backFacingCameras[backFacingCameras.length - 1].id
      : allCameras[allCameras.length - 1].id;
  }

  async function initCamera() {
    try {
      const deviceId = getCameraId(await listCameras(true), 'environment');
      stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId } });
    } catch (err) {
      return false; // ignore error
    }

    video.srcObject = stream;
    video.play();

    const res = await new Promise((resolve) => {
      video.onloadeddata = () => {
        if (video.readyState !== video.HAVE_ENOUGH_DATA) {
          resolve(false);
          return;
        }

        canvasElement.height = video.videoHeight;
        canvasElement.width = video.videoWidth;
        updateOverlay();

        resolve(true);
      };
    });

    return res;
  }

  async function initScanner(videoEl) {
    canvasElement = document.createElement('canvas');
    canvas = canvasElement.getContext('2d');

    worker = new Worker();
    worker.onmessage = (e) => {
      qrCodeContent.value = e.data;
    };

    video = videoEl;
    return initCamera();
  }

  async function startScan() {
    if (!stream) {
      await initCamera();
    }
    animationId = requestAnimationFrame(tick);
  }

  function stopScan() {
    video.pause();
    stopStream(stream);
    stream = null;
    qrCodeContent.value = null;
    cancelAnimationFrame(animationId);
    scanRegionArea.value = {};
  }

  function destroy() {
    if (stream) {
      video.pause();
      stopStream(stream);
      stream = null;
    }

    cancelAnimationFrame(animationId);

    qrCodeContent.value = null;
    video = null;
    canvasElement = null;
    canvas = null;
    scanRegionArea.value = {};
    animationId = null;

    worker.terminate();
    worker = null;
  }

  async function restart() {
    stopScan();
    await startScan();
  }

  return {
    qrCodeContent,
    startScan,
    stopScan,
    initScanner,
    destroy,
    restart,
    scanRegionArea,
  };
}
